import { useState, useEffect } from 'react';
import { API_ENDPOINTS, fetchOptions } from '../../config/api';

const HomePage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [selectedService, setSelectedService] = useState<string | null>(null);
  const [appointmentForm, setAppointmentForm] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    date: '',
    time: '',
    message: ''
  });
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [showAppointmentForm, setShowAppointmentForm] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState('');

  const services = [
    {
      name: 'Myers Cocktail',
      description: 'Energy & Immunity boost with essential vitamins and minerals',
      benefits: ['Increasing energy levels', 'Enhanced immune function', 'Improved mood', 'Better sleep quality'],
      duration: '30-45 minutes',
      price: 'Starting at $150',
      icon: 'ri-flashlight-line',
      image: '/images/myers-cocktail.jpg'
    },
    {
      name: 'B12 Injections',
      description: 'Focus & fatigue relief with vitamin B12 supplementation',
      benefits: ['Improved focus and concentration', 'Reduced fatigue', 'Enhanced metabolism', 'Better cognitive function'],
      duration: '15-20 minutes',
      price: 'Starting at $35',
      icon: 'ri-focus-3-line',
      image: '/images/b12-injection.jpg'
    },
    {
      name: 'L-Carnitine',
      description: 'Weight support and fat metabolism enhancement',
      benefits: ['Enhanced fat burning', 'Boosted metabolism', 'Increased energy', 'Appetite control support'],
      duration: '20-30 minutes',
      price: 'Starting at $75',
      icon: 'ri-heart-pulse-line',
      image: '/images/l-carnitine.jpg'
    },
    {
      name: 'Vitamin C',
      description: 'Glow & collagen boost for radiant skin',
      benefits: ['Brighter skin complexion', 'Collagen production boost', 'Antioxidant protection', 'Immune system support'],
      duration: '30-40 minutes',
      price: 'Starting at $120',
      icon: 'ri-sun-line',
      image: '/images/vitamin-c.jpg'
    },
    {
      name: 'Fat-Burn Drips',
      description: 'Comprehensive weight management and metabolism boost',
      benefits: ['Accelerated fat burning', 'Boosted metabolism', 'Increased energy', 'Appetite control support'],
      duration: '45-60 minutes',
      price: 'Starting at $180',
      icon: 'ri-fire-line',
      image: '/images/fat-burn.jpg'
    },
    {
      name: 'Hangover Recovery',
      description: 'Quick recovery from dehydration and toxin buildup',
      benefits: ['Rapid rehydration', 'Toxin elimination', 'Headache relief', 'Energy restoration'],
      duration: '30-45 minutes',
      price: 'Starting at $140',
      icon: 'ri-refresh-line',
      image: '/images/hangover-recovery.jpg'
    },
    {
      name: 'Wellness Hydration & Detox',
      description: 'Complete hydration and detoxification therapy',
      benefits: ['Deep cellular hydration', 'Toxin removal', 'Improved skin health', 'Enhanced overall wellness'],
      duration: '45-60 minutes',
      price: 'Starting at $160',
      icon: 'ri-drop-line',
      image: '/images/wellness-hydration.jpg'
    },
    {
      name: 'Glutathione',
      description: 'Skin brightening and powerful antioxidant treatment',
      benefits: ['Skin brightening and whitening', 'Powerful antioxidant protection', 'Anti-aging effects', 'Liver detoxification'],
      duration: '30-45 minutes',
      price: 'Starting at $130',
      icon: 'ri-star-line',
      image: '/images/glutathione.jpg'
    }
  ];

  const handleAppointmentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!appointmentForm.name || !appointmentForm.email || !appointmentForm.phone || !appointmentForm.service) {
      setShowSuccessMessage('Please fill in all required fields');
      setTimeout(() => setShowSuccessMessage(''), 3000);
      return;
    }

    const formData = new FormData();
    Object.entries(appointmentForm).forEach(([key, value]) => {
      formData.append(key, value);
    });

    try {
      const response = await fetch(API_ENDPOINTS.appointments, {
        method: 'POST',
        ...fetchOptions,
        body: JSON.stringify(appointmentForm)
      });

      if (response.ok) {
        setShowSuccessMessage('✅ Appointment request submitted successfully! We will contact you within 24 hours to confirm your booking.');
        setAppointmentForm({
          name: '',
          email: '',
          phone: '',
          service: '',
          date: '',
          time: '',
          message: ''
        });
        setShowAppointmentForm(false);
        setTimeout(() => setShowSuccessMessage(''), 6000);
      } else {
        setShowSuccessMessage('❌ Failed to submit appointment request. Please try again or call us directly.');
        setTimeout(() => setShowSuccessMessage(''), 4000);
      }
    } catch (error) {
      setShowSuccessMessage('❌ Error submitting form. Please try again or contact us by phone.');
      setTimeout(() => setShowSuccessMessage(''), 4000);
    }
  };

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!contactForm.name || !contactForm.email || !contactForm.message) {
      setShowSuccessMessage('Please fill in all required fields');
      setTimeout(() => setShowSuccessMessage(''), 3000);
      return;
    }

    const formData = new FormData();
    Object.entries(contactForm).forEach(([key, value]) => {
      formData.append(key, value);
    });

    try {
      const response = await fetch(API_ENDPOINTS.contact, {
        method: 'POST',
        ...fetchOptions,
        body: JSON.stringify(contactForm)
      });

      if (response.ok) {
        setShowSuccessMessage('✅ Message sent successfully! We will get back to you within 24 hours.');
        setContactForm({
          name: '',
          email: '',
          phone: '',
          message: ''
        });
        setShowContactForm(false);
        setTimeout(() => setShowSuccessMessage(''), 6000);
      } else {
        setShowSuccessMessage('❌ Failed to send message. Please try again or call us directly.');
        setTimeout(() => setShowSuccessMessage(''), 4000);
      }
    } catch (error) {
      setShowSuccessMessage('❌ Error submitting form. Please try again or contact us by phone.');
      setTimeout(() => setShowSuccessMessage(''), 4000);
    }
  };

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newsletterEmail) {
      setShowSuccessMessage('Please enter your email address');
      setTimeout(() => setShowSuccessMessage(''), 3000);
      return;
    }

    try {
      const response = await fetch(API_ENDPOINTS.newsletter, {
        method: 'POST',
        ...fetchOptions,
        body: JSON.stringify({ email: newsletterEmail })
      });

      if (response.ok) {
        setShowSuccessMessage('✅ Successfully subscribed to our newsletter! Welcome to the Soft Meds wellness community.');
        setNewsletterEmail('');
        setTimeout(() => setShowSuccessMessage(''), 6000);
      } else {
        setShowSuccessMessage('❌ Failed to subscribe. Please try again or contact us directly.');
        setTimeout(() => setShowSuccessMessage(''), 4000);
      }
    } catch (error) {
      setShowSuccessMessage('❌ Error subscribing. Please try again or contact us directly.');
      setTimeout(() => setShowSuccessMessage(''), 4000);
    }
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Success Message */}
      {showSuccessMessage && (
        <div className={`fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4`}>
          <div className={`max-w-md w-full px-8 py-6 rounded-2xl shadow-2xl animate-fadeIn border-l-4 ${showSuccessMessage.includes('✅')
            ? 'bg-green-50 text-green-800 border-green-500'
            : 'bg-red-50 text-red-800 border-red-500'
            }`}>
            <div className="flex items-start">
              <div className="flex-shrink-0">
                {showSuccessMessage.includes('✅') ? (
                  <i className="ri-check-circle-fill text-green-500 text-2xl"></i>
                ) : (
                  <i className="ri-error-warning-fill text-red-500 text-2xl"></i>
                )}
              </div>
              <div className="ml-4 flex-1">
                <p className="text-base font-medium leading-relaxed">
                  {showSuccessMessage}
                </p>
              </div>
              <button
                onClick={() => setShowSuccessMessage('')}
                className="ml-4 flex-shrink-0 text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm shadow-lg z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center">
              <div className="flex items-center space-x-4">
                <img
                  src="/images/logo.png"
                  alt="Soft Meds by Erhu Medical Aesthetics"
                  className="h-16 w-auto object-contain"
                />
              </div>
            </div>

            <div className="hidden md:block">
              <div className="ml-10 flex items-baseline space-x-8">
                <button onClick={() => scrollToSection('home')} className="text-gray-700 hover:text-amber-600 px-3 py-2 text-sm font-medium cursor-pointer whitespace-nowrap transition-colors">
                  Home
                </button>
                <button onClick={() => scrollToSection('services')} className="text-gray-700 hover:text-amber-600 px-3 py-2 text-sm font-medium cursor-pointer whitespace-nowrap transition-colors">
                  Services
                </button>
                <button onClick={() => scrollToSection('about')} className="text-gray-700 hover:text-amber-600 px-3 py-2 text-sm font-medium cursor-pointer whitespace-nowrap transition-colors">
                  About
                </button>
                <button onClick={() => scrollToSection('contact')} className="text-gray-700 hover:text-amber-600 px-3 py-2 text-sm font-medium cursor-pointer whitespace-nowrap transition-colors">
                  Contact
                </button>
                <button
                  onClick={() => setShowAppointmentForm(true)}
                  className="bg-gradient-to-r from-amber-600 to-yellow-600 text-white px-8 py-3 rounded-full hover:from-amber-700 hover:to-yellow-700 transition-all duration-300 cursor-pointer whitespace-nowrap shadow-lg"
                >
                  Book Appointment
                </button>
              </div>
            </div>

            <div className="md:hidden">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="text-gray-700 hover:text-amber-600 cursor-pointer"
              >
                <i className={`ri-${isMenuOpen ? 'close' : 'menu'}-line text-2xl`}></i>
              </button>
            </div>
          </div>
        </div>

        {isMenuOpen && (
          <div className="md:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-white border-t shadow-lg">
              <button onClick={() => scrollToSection('home')} className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-amber-600 cursor-pointer">
                Home
              </button>
              <button onClick={() => scrollToSection('services')} className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-amber-600 cursor-pointer">
                Services
              </button>
              <button onClick={() => scrollToSection('about')} className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-amber-600 cursor-pointer">
                About
              </button>
              <button onClick={() => scrollToSection('contact')} className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-amber-600 cursor-pointer">
                Contact
              </button>
              <button
                onClick={() => setShowAppointmentForm(true)}
                className="block w-full text-left bg-gradient-to-r from-amber-600 to-yellow-600 text-white px-3 py-2 rounded-lg hover:from-amber-700 hover:to-yellow-700 transition-all duration-300 cursor-pointer"
              >
                Book Appointment
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('/images/hero_bg.webp')`
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="flex items-center justify-center min-h-screen">
            <div className="text-white text-center max-w-4xl">
              <div className="mb-6">
                <span className="inline-block bg-amber-500/20 text-amber-300 px-4 py-2 rounded-full text-sm font-medium mb-4">
                  Premium IV Therapy & Wellness
                </span>
              </div>

              <h1 className="text-6xl md:text-8xl font-bold leading-tight mb-6">
                <span className="text-amber-400">Enhance Your</span><br />
                <span className="text-white">Natural Beauty</span>
              </h1>

              <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed max-w-3xl mx-auto">
                Premium IV therapy and wellness treatments administered by licensed nurses using sterile equipment, tailored to your individual health and beauty goals
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-8 justify-center">
                <button
                  onClick={() => setShowAppointmentForm(true)}
                  className="bg-gradient-to-r from-amber-500 to-yellow-600 text-white px-10 py-4 rounded-full text-lg font-semibold hover:from-amber-600 hover:to-yellow-700 transition-all duration-300 cursor-pointer whitespace-nowrap shadow-xl transform hover:scale-105"
                >
                  Book Your Treatment
                </button>
                <button
                  onClick={() => scrollToSection('services')}
                  className="border-2 border-white text-white px-10 py-4 rounded-full text-lg font-semibold hover:bg-white hover:text-amber-600 transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  View Services
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 bg-gradient-to-br from-gray-50 to-amber-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <span className="inline-block bg-amber-100 text-amber-600 px-4 py-2 rounded-full text-sm font-medium mb-4">
              Our Premium Services
            </span>
            <h2 className="text-5xl font-bold text-gray-900 mb-6">Transform Your Wellness Journey</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Discover our comprehensive range of IV therapy and wellness treatments designed to enhance your health, beauty, and overall well-being with personalized care from licensed medical professionals
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-white rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500 cursor-pointer transform hover:-translate-y-2 group"
                onClick={() => setSelectedService(selectedService === service.name ? null : service.name)}
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute top-4 right-4">
                    <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                      <i className={`${service.icon} text-white text-xl`}></i>
                    </div>
                  </div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="text-xl font-bold text-white mb-1">{service.name}</h3>
                    <p className="text-amber-200 text-sm font-semibold">{service.price}</p>
                  </div>
                </div>

                <div className="p-6">
                  <p className="text-gray-600 mb-4 leading-relaxed">{service.description}</p>

                  {selectedService === service.name && (
                    <div className="mt-4 space-y-4 animate-fadeIn">
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                          <i className="ri-star-line text-amber-600 mr-2"></i>
                          Benefits:
                        </h4>
                        <ul className="text-sm text-gray-600 space-y-2">
                          {service.benefits.map((benefit, idx) => (
                            <li key={idx} className="flex items-start">
                              <i className="ri-check-line text-amber-600 mr-2 mt-0.5 flex-shrink-0"></i>
                              {benefit}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="flex justify-between items-center pt-3 border-t border-gray-200">
                        <span className="text-sm text-gray-500 flex items-center">
                          <i className="ri-time-line mr-1"></i>
                          {service.duration}
                        </span>
                      </div>
                    </div>
                  )}

                  <div className="text-center mt-6">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setAppointmentForm(prev => ({ ...prev, service: service.name }));
                        setShowAppointmentForm(true);
                      }}
                      className="bg-gradient-to-r from-amber-600 to-yellow-600 text-white px-8 py-3 rounded-full hover:from-amber-700 hover:to-yellow-700 transition-all duration-300 cursor-pointer whitespace-nowrap shadow-lg transform hover:scale-105"
                    >
                      Book Now
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <span className="inline-block bg-amber-100 text-amber-600 px-4 py-2 rounded-full text-sm font-medium mb-6">
                Why Choose Soft Meds
              </span>
              <h2 className="text-5xl font-bold text-gray-900 mb-8">Excellence in Medical Aesthetics</h2>
              <p className="text-lg text-gray-600 mb-10 leading-relaxed">
                At Soft Meds by Erhu Medical Aesthetics, we combine medical expertise with personalized care to help you achieve optimal wellness and beauty results. Our team of licensed medical professionals is dedicated to providing safe, effective treatments in a comfortable, welcoming environment.
              </p>

              <div className="space-y-8">
                <div className="flex items-start group">
                  <div className="w-16 h-16 bg-gradient-to-br from-amber-100 to-yellow-200 rounded-2xl flex items-center justify-center mr-6 flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                    <i className="ri-user-heart-line text-2xl text-amber-600"></i>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">Licensed Medical Professionals</h3>
                    <p className="text-gray-600 leading-relaxed">Our licensed nurses have extensive training and experience in medical aesthetics and IV therapy treatments, ensuring your safety and optimal results with every visit.</p>
                  </div>
                </div>

                <div className="flex items-start group">
                  <div className="w-16 h-16 bg-gradient-to-br from-amber-100 to-yellow-200 rounded-2xl flex items-center justify-center mr-6 flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                    <i className="ri-microscope-line text-2xl text-amber-600"></i>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">Sterile Equipment &amp; Safety</h3>
                    <p className="text-gray-600 leading-relaxed">We use only the highest quality, sterile medical equipment and follow strict safety protocols for every treatment to ensure your wellbeing and peace of mind.</p>
                  </div>
                </div>

                <div className="flex items-start group">
                  <div className="w-16 h-16 bg-gradient-to-br from-amber-100 to-yellow-200 rounded-2xl flex items-center justify-center mr-6 flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                    <i className="ri-heart-pulse-line text-2xl text-amber-600"></i>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">Personalized Care</h3>
                    <p className="text-gray-600 leading-relaxed">Every treatment is customized and tailored to your unique health goals and individual needs for maximum effectiveness and optimal results.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="order-1 lg:order-2 relative">
              <div className="grid grid-cols-2 gap-6">
                <img
                  src="/images/about-img1.jpg"
                  alt="Licensed medical professional"
                  className="rounded-3xl shadow-2xl object-cover w-full h-80"
                />
                <img
                  src="/images/about-img2.jpg"
                  alt="Medical spa treatment room"
                  className="rounded-3xl shadow-2xl object-cover w-full h-80 mt-12"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-gradient-to-br from-amber-400 to-yellow-600 rounded-full opacity-20"></div>
              <div className="absolute -top-6 -right-6 w-24 h-24 bg-gradient-to-br from-amber-400 to-yellow-600 rounded-full opacity-20"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 bg-gradient-to-br from-amber-50 to-yellow-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block bg-amber-100 text-amber-600 px-4 py-2 rounded-full text-sm font-medium mb-4">
              Our Process
            </span>
            <h2 className="text-5xl font-bold text-gray-900 mb-6">Your Journey to Wellness</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Experience our streamlined process designed for your comfort and optimal results
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center group">
              <div className="w-20 h-20 bg-gradient-to-br from-amber-500 to-yellow-600 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                <i className="ri-calendar-check-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">1. Book Consultation</h3>
              <p className="text-gray-600">Schedule your personalized consultation to discuss your wellness goals and treatment options.</p>
            </div>

            <div className="text-center group">
              <div className="w-20 h-20 bg-gradient-to-br from-amber-500 to-yellow-600 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                <i className="ri-user-search-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">2. Assessment</h3>
              <p className="text-gray-600">Our licensed nurses assess your health and create a customized treatment plan tailored to you.</p>
            </div>

            <div className="text-center group">
              <div className="w-20 h-20 bg-gradient-to-br from-amber-500 to-yellow-600 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                <i className="ri-heart-pulse-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">3. Treatment</h3>
              <p className="text-gray-600">Relax in our comfortable environment while receiving your personalized IV therapy treatment.</p>
            </div>

            <div className="text-center group">
              <div className="w-20 h-20 bg-gradient-to-br from-amber-500 to-yellow-600 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                <i className="ri-refresh-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">4. Follow-up</h3>
              <p className="text-gray-600">We monitor your progress and adjust your treatment plan for continued optimal results.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-16 bg-gradient-to-r from-amber-600 to-yellow-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-4">Stay Updated on Wellness</h2>
          <p className="text-xl text-amber-100 mb-8">Get exclusive offers, wellness tips, and treatment updates delivered to your inbox</p>

          <form id="newsletter-form" onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
            <input
              type="email"
              name="email"
              value={newsletterEmail}
              onChange={(e) => setNewsletterEmail(e.target.value)}
              placeholder="Enter your email address"
              className="flex-1 px-6 py-4 rounded-full text-gray-900 focus:outline-none focus:ring-4 focus:ring-white/30"
              required
            />
            <button
              type="submit"
              className="bg-white text-amber-600 px-8 py-4 rounded-full font-semibold hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              Subscribe
            </button>
          </form>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block bg-amber-100 text-amber-600 px-4 py-2 rounded-full text-sm font-medium mb-4">
              Get In Touch
            </span>
            <h2 className="text-5xl font-bold text-gray-900 mb-6">Ready to Transform Your Wellness?</h2>
            <p className="text-xl text-gray-600">Contact us today to schedule your consultation and begin your journey to optimal health and beauty.</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <div className="space-y-10">
                <div className="flex items-start group">
                  <div className="w-16 h-16 bg-gradient-to-br from-amber-100 to-yellow-200 rounded-2xl flex items-center justify-center mr-6 flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                    <i className="ri-map-pin-line text-2xl text-amber-600"></i>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">Visit Our Clinic</h3>
                    <p className="text-gray-600 text-lg leading-relaxed">
                      45 Gateway Blvd, Unit 114<br />
                      Brampton, ON L6T 4X2<br />
                      Canada
                    </p>
                  </div>
                </div>

                <div className="flex items-start group">
                  <div className="w-16 h-16 bg-gradient-to-br from-amber-100 to-yellow-200 rounded-2xl flex items-center justify-center mr-6 flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                    <i className="ri-phone-line text-2xl text-amber-600"></i>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">Call Us</h3>
                    <p className="text-gray-600 text-lg">
                      <a href="tel:9052262619" className="hover:text-amber-400 cursor-pointer transition-colors">905 226 2619</a><br />
                      <a href="tel:6477742702" className="hover:text-amber-400 cursor-pointer transition-colors">647 774 2702</a>
                    </p>
                  </div>
                </div>

                <div className="flex items-start group">
                  <div className="w-16 h-16 bg-gradient-to-br from-amber-100 to-yellow-200 rounded-2xl flex items-center justify-center mr-6 flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                    <i className="ri-mail-line text-2xl text-amber-600"></i>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">Email Us</h3>
                    <p className="text-gray-600 text-lg">
                      <a href="mailto:softmeds.Erhu@gmail.com" className="hover:text-amber-400 cursor-pointer transition-colors">softmeds.Erhu@gmail.com</a>
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-12 flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => setShowContactForm(true)}
                  className="bg-gradient-to-r from-amber-600 to-yellow-600 text-white px-10 py-4 rounded-full text-lg hover:from-amber-700 hover:to-yellow-700 transition-all duration-300 cursor-pointer whitespace-nowrap shadow-xl transform hover:scale-105"
                >
                  Send Message
                </button>
                <button
                  onClick={() => setShowAppointmentForm(true)}
                  className="border-2 border-amber-600 text-amber-600 px-10 py-4 rounded-full text-lg hover:bg-amber-600 hover:text-white transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  Book Appointment
                </button>
              </div>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-amber-50 rounded-3xl p-8 shadow-xl">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2886.1234567890123!2d-79.7234567890123!3d43.7234567890123!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2s45%20Gateway%20Blvd%2C%20Brampton%2C%20ON%20L6T%204X2!5e0!3m2!1sen!2sca!4v1234567890123!5m2!1sen!2sca"
                width="100%"
                height="400"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="rounded-2xl"
                title="Soft Meds by Erhu Medical Aesthetics Location"
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <div className="flex items-center space-x-4 mb-6">
                <h3 className="text-3xl font-bold" style={{ fontFamily: '"Pacifico", serif' }}>
                  Soft Meds
                </h3>
              </div>

              <p className="text-gray-300 mb-6 text-lg leading-relaxed">
                Premium IV therapy and wellness treatments by licensed medical professionals. Experience the difference of personalized care in a luxurious, safe environment in Brampton, Ontario.
              </p>
              <div className="flex space-x-6">
                <a href="#" className="text-gray-300 hover:text-amber-400 cursor-pointer transition-colors">
                  <i className="ri-facebook-fill text-2xl"></i>
                </a>
                <a href="#" className="text-gray-300 hover:text-amber-400 cursor-pointer transition-colors">
                  <i className="ri-instagram-line text-2xl"></i>
                </a>
                <a href="#" className="text-gray-300 hover:text-amber-400 cursor-pointer transition-colors">
                  <i className="ri-twitter-line text-2xl"></i>
                </a>
                <a href="#" className="text-gray-300 hover:text-amber-400 cursor-pointer transition-colors">
                  <i className="ri-linkedin-line text-2xl"></i>
                </a>
              </div>
            </div>

            <div>
              <h4 className="text-xl font-semibold mb-6">Quick Links</h4>
              <ul className="space-y-3">
                <li><button onClick={() => scrollToSection('home')} className="text-gray-300 hover:text-amber-400 cursor-pointer transition-colors">Home</button></li>
                <li><button onClick={() => scrollToSection('services')} className="text-gray-300 hover:text-amber-400 cursor-pointer transition-colors">Services</button></li>
                <li><button onClick={() => scrollToSection('about')} className="text-gray-300 hover:text-amber-400 cursor-pointer transition-colors">About</button></li>
                <li><button onClick={() => scrollToSection('contact')} className="text-gray-300 hover:text-amber-400 cursor-pointer transition-colors">Contact</button></li>
              </ul>
            </div>

            <div>
              <h4 className="text-xl font-semibold mb-6">Contact Info</h4>
              <div className="space-y-3 text-gray-300">
                <p className="flex items-start">
                  <i className="ri-map-pin-line mr-2 mt-1 text-amber-400"></i>
                  45 Gateway Blvd, Unit 114<br />
                  Brampton, ON L6T 4X2
                </p>
                <p className="flex items-center">
                  <i className="ri-phone-line mr-2 text-amber-400"></i>
                  905 226 2619 | 647 774 2702
                </p>
                <p className="flex items-center">
                  <i className="ri-mail-line mr-2 text-amber-400"></i>
                  <a href="mailto:softmeds.Erhu@gmail.com" className="hover:text-amber-400 cursor-pointer transition-colors">softmeds.Erhu@gmail.com</a>
                </p>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-700 mt-12 pt-8 text-center">
            <p className="text-gray-300 text-lg">
              © 2024 Soft Meds by Erhu Medical Aesthetics. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Appointment Booking Modal */}
      {showAppointmentForm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-gray-900">Book Appointment</h3>
              <button
                onClick={() => setShowAppointmentForm(false)}
                className="text-gray-500 hover:text-gray-700 cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <form id="appointment-form" onSubmit={handleAppointmentSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  name="name"
                  value={appointmentForm.name}
                  onChange={(e) => setAppointmentForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input
                  type="email"
                  name="email"
                  value={appointmentForm.email}
                  onChange={(e) => setAppointmentForm(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                <input
                  type="tel"
                  name="phone"
                  value={appointmentForm.phone}
                  onChange={(e) => setAppointmentForm(prev => ({ ...prev, phone: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Service *</label>
                <select
                  name="service"
                  value={appointmentForm.service}
                  onChange={(e) => setAppointmentForm(prev => ({ ...prev, service: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm pr-8"
                  required
                >
                  <option value="">Select a service</option>
                  {services.map((service, index) => (
                    <option key={index} value={service.name}>{service.name}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Date</label>
                  <input
                    type="date"
                    name="date"
                    value={appointmentForm.date}
                    onChange={(e) => setAppointmentForm(prev => ({ ...prev, date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Time</label>
                  <input
                    type="time"
                    name="time"
                    value={appointmentForm.time}
                    onChange={(e) => setAppointmentForm(prev => ({ ...prev, time: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Additional Message</label>
                <textarea
                  name="message"
                  value={appointmentForm.message}
                  onChange={(e) => setAppointmentForm(prev => ({ ...prev, message: e.target.value.slice(0, 500) }))}
                  rows={3}
                  maxLength={500}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                  placeholder="Any specific concerns or questions..."
                ></textarea>
                <p className="text-xs text-gray-500 mt-1">{appointmentForm.message.length}/500 characters</p>
              </div>

              <button
                type="submit"
                className="w-full bg-amber-600 text-white py-3 rounded-lg hover:bg-amber-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                Submit Appointment Request
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Contact Form Modal */}
      {showContactForm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-gray-900">Send Message</h3>
              <button
                onClick={() => setShowContactForm(false)}
                className="text-gray-500 hover:text-gray-700 cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <form id="contact-form" onSubmit={handleContactSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  name="name"
                  value={contactForm.name}
                  onChange={(e) => setContactForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input
                  type="email"
                  name="email"
                  value={contactForm.email}
                  onChange={(e) => setContactForm(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={contactForm.phone}
                  onChange={(e) => setContactForm(prev => ({ ...prev, phone: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
                <textarea
                  name="message"
                  value={contactForm.message}
                  onChange={(e) => setContactForm(prev => ({ ...prev, message: e.target.value.slice(0, 500) }))}
                  rows={4}
                  maxLength={500}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                  placeholder="How can we help you?"
                  required
                ></textarea>
                <p className="text-xs text-gray-500 mt-1">{contactForm.message.length}/500 characters</p>
              </div>

              <button
                type="submit"
                className="w-full bg-amber-600 text-white py-3 rounded-lg hover:bg-amber-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                Send Message
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePage;
